"use client";
import Link from "next/link";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import { usePathname } from "next/navigation";
import { Menu, ShoppingCart, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cartTotalItemsAtom, cartOpenAtom, mobileMenuOpenAtom } from "@/store";
import { useTheme } from "next-themes";
import { SearchDialog } from "./search-dialog";
import { navigation } from "./navigation";
import { ApplicatorsCombobox } from "./applicators-combobox";
import { ProfileMenu } from "./profile-menu";
import { MobileProfileBlock } from "./mobile-profile-block";
import { NotificationBanner } from "./notification-banner";
import LogoText from "./logo-text";

interface HeaderProps {
  /** Optional override for transparent text scheme; when omitted auto-detection is used. */
  transparentTextScheme?: "light" | "dark";
}

export function Header({ transparentTextScheme }: HeaderProps) {
  const [cartTotalItems] = useAtom(cartTotalItemsAtom);
  const [, setCartOpen] = useAtom(cartOpenAtom);
  const [mobileMenuOpen, setMobileMenuOpen] = useAtom(mobileMenuOpenAtom);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // theme access removed (unused)
  useTheme();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 16);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const activeBackground = scrolled || mobileMenuOpen;
  const transparentAtTop = !activeBackground;

  const [autoScheme, setAutoScheme] = useState<"light" | "dark">("light");
  const pathname = usePathname();

  const computeScheme = useCallback(() => {
    if (!transparentAtTop) return;
    const hinted = document.querySelector(
      "section[data-header-scheme], main[data-header-scheme]"
    ) as HTMLElement | null;
    const hintedValue = hinted?.getAttribute("data-header-scheme") as
      | "light"
      | "dark"
      | null;
    if (hintedValue === "light" || hintedValue === "dark") {
      setAutoScheme(hintedValue);
      return;
    }
    const candidate = document.querySelector(
      "main > section:first-of-type, section.hero, .hero"
    ) as HTMLElement | null;
    const el = candidate || document.documentElement;
    let bg = window.getComputedStyle(el).backgroundColor;
    if (bg === "transparent" || bg === "rgba(0, 0, 0, 0)") {
      bg = window.getComputedStyle(document.body).backgroundColor;
    }
    const rootStyles = window.getComputedStyle(document.documentElement);
    const parseColorToLuminance = (color: string): number | null => {
      const varMatch = color.match(/var\((--[^)]+)\)/);
      if (varMatch) {
        const varValue = rootStyles.getPropertyValue(varMatch[1]).trim();
        const parts = varValue.split(/\s+/);
        if (parts.length >= 3) {
          const [hRaw, sRaw, lRaw] = parts;
          const h = parseFloat(hRaw);
          const s = parseFloat(sRaw.replace("%", "")) / 100;
          const l = parseFloat(lRaw.replace("%", "")) / 100;
          const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
          };
          const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
          const p = 2 * l - q;
          const hk = (h % 360) / 360;
          const r = hue2rgb(p, q, hk + 1 / 3);
          const g = hue2rgb(p, q, hk);
          const b = hue2rgb(p, q, hk - 1 / 3);
          const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          return luminance;
        }
      }
      const rgba = color.match(/rgba?\(([^)]+)\)/);
      if (rgba) {
        const [r, g, b] = rgba[1]
          .split(",")
          .slice(0, 3)
          .map((p) => parseFloat(p.trim()));
        const toLinear = (c: number) => {
          c /= 255;
          return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };
        const rl = toLinear(r);
        const gl = toLinear(g);
        const bl = toLinear(b);
        return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
      }
      return null;
    };
    const luminance = parseColorToLuminance(bg);
    if (luminance != null) {
      setAutoScheme(luminance > 0.65 ? "dark" : "light");
    } else {
      setAutoScheme("light");
    }
  }, [transparentAtTop]);

  useEffect(() => {
    requestAnimationFrame(computeScheme);
    const t = setTimeout(computeScheme, 120);
    return () => clearTimeout(t);
  }, [pathname, transparentAtTop, computeScheme]);

  // Force dark scheme (dark text) on auth pages when transparent to avoid white-on-white.
  const isAuthPage = pathname === "/signin" || pathname === "/signup";
  const isDashboard =
    pathname === "/dashboard" || pathname.startsWith("/dashboard/");
  const isHome = pathname === "/";
  const isAbout = pathname === "/about"; // Force white when transparent at top on About page hero
  // When on homepage or about page with transparent hero, keep nav text white until scrolled.
  const forceWhite = (isHome || isAbout) && !scrolled && !mobileMenuOpen;
  // Force dark scheme (black text in light mode) on auth pages and dashboard pages when transparent
  const effectiveScheme =
    transparentTextScheme || (isAuthPage || isDashboard ? "dark" : autoScheme);

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50">
        <NotificationBanner />
        <header
          className={`transition-[background-color,backdrop-filter,border-color] duration-300 ${
            activeBackground
              ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm dark:bg-background/60"
              : "bg-transparent border-b border-transparent"
          }`}
        >
          <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <div className="flex items-center">
                <Link href="/" className="flex items-center space-x-2">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative h-40 w-40"
                  >
                    <LogoText
                      forceScheme={
                        transparentAtTop && forceWhite ? "dark" : undefined
                      }
                    />
                  </motion.div>
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-6">
                  {(Array.isArray(navigation) ? navigation : []).map((item) => {
                    if (item.name === "Find Applicators") {
                      return (
                        <ApplicatorsCombobox
                          key={item.name}
                          transparentAtTop={transparentAtTop}
                          scheme={effectiveScheme}
                          forceWhite={forceWhite}
                        />
                      );
                    }
                    return (
                      <Link
                        key={item.name}
                        href={item.href}
                        className={`px-3 py-2 text-sm font-medium transition-colors relative group ${
                          transparentAtTop
                            ? forceWhite
                              ? "text-white/80 hover:text-white"
                              : effectiveScheme === "dark"
                              ? "text-foreground/70 hover:text-foreground"
                              : "text-black/70 hover:text-black dark:text-white/70 dark:hover:text-white"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {item.name}
                        <motion.div
                          className={`absolute bottom-0 left-0 w-full h-0.5 origin-left ${
                            transparentAtTop
                              ? forceWhite
                                ? "bg-white"
                                : effectiveScheme === "dark"
                                ? "bg-foreground"
                                : "bg-black dark:bg-white"
                              : "bg-foreground"
                          }`}
                          initial={{ scaleX: 0 }}
                          whileHover={{ scaleX: 1 }}
                          transition={{ duration: 0.2 }}
                        />
                      </Link>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchOpen(true)}
                  aria-label="Open site search (⌘K)"
                  className={`hidden sm:flex transition-opacity duration-200 ${
                    transparentAtTop
                      ? forceWhite
                        ? "text-white hover:text-white hover:bg-transparent hover:opacity-80"
                        : effectiveScheme === "dark"
                        ? "text-foreground hover:text-foreground hover:bg-transparent hover:opacity-80"
                        : "text-black dark:text-white hover:text-black dark:hover:text-white hover:bg-transparent hover:opacity-80"
                      : ""
                  }`}
                >
                  <Search className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCartOpen(true)}
                  aria-label="Open shopping cart"
                  className={`relative transition-opacity duration-200 ${
                    transparentAtTop
                      ? forceWhite
                        ? "text-white hover:text-white hover:bg-transparent hover:opacity-80"
                        : effectiveScheme === "dark"
                        ? "text-foreground hover:text-foreground hover:bg-transparent hover:opacity-80"
                        : "text-black dark:text-white hover:text-black dark:hover:text-white hover:bg-transparent hover:opacity-80"
                      : ""
                  }`}
                >
                  <ShoppingCart className="h-4 w-4" />
                  {cartTotalItems > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
                    >
                      {cartTotalItems}
                    </Badge>
                  )}
                </Button>
                {/* Hide profile dropdown on mobile; actions moved into sheet */}
                <div className="hidden md:flex">
                  <ProfileMenu
                    transparent={transparentAtTop}
                    scheme={effectiveScheme}
                  />
                </div>

                <div className="md:hidden">
                  <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                    <SheetTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Open menu"
                        className={
                          transparentAtTop
                            ? forceWhite
                              ? "text-white hover:text-white hover:bg-transparent hover:opacity-80 transition-opacity duration-200"
                              : effectiveScheme === "dark"
                              ? "text-foreground hover:text-foreground hover:bg-transparent hover:opacity-80 transition-opacity duration-200"
                              : "text-black dark:text-white hover:text-black dark:hover:text-white hover:bg-transparent hover:opacity-80 transition-opacity duration-200"
                            : undefined
                        }
                      >
                        <Menu className="h-4 w-4" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side="right"
                      className="w-[300px] sm:w-[400px]"
                    >
                      <div className="flex flex-col space-y-6 mt-6">
                        <div className="flex flex-col space-y-4">
                          {(Array.isArray(navigation) ? navigation : []).map(
                            (item) => {
                              if (item.name === "Find Applicators") {
                                return (
                                  <div key={item.name} className="space-y-2">
                                    <p className="px-3 text-sm uppercase tracking-wider text-muted-foreground/70">
                                      Applicators
                                    </p>
                                    <Link
                                      href="/applicators"
                                      onClick={() => setMobileMenuOpen(false)}
                                      className="px-3 py-2 text-sm font-medium text-primary hover:underline"
                                    >
                                      Browse Cities →
                                    </Link>
                                  </div>
                                );
                              }
                              return (
                                <Link
                                  key={item.name}
                                  href={item.href}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className={`px-3 py-2 text-lg font-medium transition-colors ${
                                    transparentAtTop
                                      ? effectiveScheme === "dark"
                                        ? "text-foreground hover:text-foreground"
                                        : "text-black dark:text-white hover:text-black dark:hover:text-white/90"
                                      : "text-muted-foreground hover:text-foreground"
                                  }`}
                                >
                                  {item.name}
                                </Link>
                              );
                            }
                          )}
                        </div>
                        <div className="h-px bg-border" />

                        {/* Mobile profile / auth & theme actions */}
                        <div className="px-4">
                          <div className="px-3 py-2 rounded-md border bg-card/50 backdrop-blur-sm">
                            <MobileProfileBlock
                              onNavigate={() => setMobileMenuOpen(false)}
                              scheme={effectiveScheme}
                            />
                          </div>
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
              </div>
            </div>
          </nav>
        </header>
      </div>
      {/* Search dialog mounted at end to avoid z-index conflicts */}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}

// ApplicatorsDropdown replaced by ApplicatorsCombobox component (searchable)
